import { useCallback } from 'react';
import { useCurrentUser, useCurrentUserId, useProjectCapabilities } from '@/entities/user';
import { BriefComment, type MentionOption } from '@/entities/project-comment';
import { DeleteConfirmationModal } from '@/shared/ui';
import { AnnotatedCommentInput } from '../AnnotatedCommentInput';
import { HighlightCommentComposerChip } from '../HighlightCommentComposerChip';
import { scrollBriefSectionIntoView } from '../../lib/briefSectionElements';
import { useReducedMotion } from 'motion/react';
import { formatRelativeTime } from '../../lib/formatRelativeTime';
import { commentCardClassName } from '../commentCardClassName';
import { CommentRepliesSkeleton } from '../CommentThread/CommentRepliesSkeleton';
import { parseAnnotation } from '../../model/parseAnnotation';
import {
  briefSectionHighlights,
  useCommentEdit,
  useIsCommentSelected,
  usePendingTextAnchor,
  useSelectionActions,
} from '../../model/comments.store';
import { useMountEffect } from '@/shared/hooks/useMountEffect';
import { useThreadReplies } from '../CommentThread/useThreadReplies';
import { useThreadActions } from '../CommentThread/useThreadActions';
import type { CommentData, CommentThreadActions } from '../CommentThread/CommentThread.types';
import { useTranslation } from 'react-i18next';

const EMPTY_DOC: Record<string, unknown> = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

interface Props {
  comment: CommentData;
  depth?: number;
  actions: CommentThreadActions;
  /** `@` dropdown options for the reply/edit composers (agents + people). */
  mentionOptions?: MentionOption[];
  /** True when this is the first reply in its thread — drops the top padding. */
  isFirstReply?: boolean;
}

/**
 * Brief comment thread. Renders the `BriefComment` leaf and the
 * `AnnotatedCommentInput` composer, and owns the brief-only text-highlight
 * concerns: the "Highlight" pill (derived from the comment's text annotation),
 * and anchoring a highlight to a ROOT comment while it's being edited.
 *
 * Calls NONE of the annotation/media-player hooks — briefs are never wrapped in
 * a <MediaPlayerProvider> and those hooks throw without one. The shared
 * `useThreadReplies`/`useThreadActions` hooks are annotation-agnostic; the
 * highlight payload flows through `handleEditSave` → `actions.updateComment`.
 */
export function BriefCommentThread({
  comment,
  depth = 0,
  actions,
  mentionOptions,
  isFirstReply = false,
}: Props) {
  const { t } = useTranslation('projects');
  const { t: tCommon } = useTranslation('common');

  const currentUserId = useCurrentUserId();
  const currentUser = useCurrentUser();
  const { pendingTextAnchor, setPendingTextAnchor, clearPendingTextAnchor } =
    usePendingTextAnchor();
  const { selectComment, clearSelectedComment } = useSelectionActions();
  const { startCommentEdit, endCommentEdit } = useCommentEdit();
  // Closing the rail unmounts an open edit composer without cancelling or saving it.
  useMountEffect(() => () => endCommentEdit(comment.id));
  const isCommentSelected = useIsCommentSelected(comment.id);
  const shouldReduceMotion = useReducedMotion();

  // Agent comments (POC): resolve the stored agent id slug to a display
  // identity (name + avatar). Falls back to the raw author payload.
  const agentIdentity =
    comment.authorType === 'agent' ? actions.resolveAgentIdentity?.(comment.createdBy) : undefined;
  const authorName = agentIdentity?.name ?? comment.author.name;
  const avatarUrl = comment.author.avatarUrl;

  const parsed = parseAnnotation(comment.annotation);
  const textAnnotation = parsed?.kind === 'text' ? parsed : null;
  const isDetached = textAnnotation?.detached === true;
  const hasActiveAnchor = !!textAnnotation && !isDetached;

  const isRoot = depth === 0;
  const canComment = useProjectCapabilities().canComment;
  const isAuthor = !!currentUserId && currentUserId === comment.createdBy;
  const canDelete = (isAuthor || actions.canDeleteComment?.(comment) === true) && canComment;
  const isSelected = isRoot && isCommentSelected;

  // Editing a root comment turns its highlight into the editable draft: it
  // renders as pending, re-selecting moves it, clicking away clears it.
  const handleEditStart = useCallback(() => {
    startCommentEdit(comment.id);
    if (!isRoot || !textAnnotation || textAnnotation.detached) return;
    const { sectionId, from, to } = textAnnotation;
    pendingTextAnchor?.cancelDraft();
    const controls = briefSectionHighlights.get(sectionId);
    controls?.remove(comment.id);
    controls?.setDraft({ from, to });
    setPendingTextAnchor({
      sectionId,
      // An edit's anchor confirms the draft under this comment's id, so it must not survive
      // the rail closing and resurface on the new-comment composer.
      targetId: null,
      from,
      to,
      // Re-anchoring an existing highlight during a comment edit — no fresh
      // selection text to capture; agent grounding only applies on new comments.
      selectedText: '',
      applyMark: () => {
        briefSectionHighlights.get(sectionId)?.confirmDraft(comment.id);
        clearPendingTextAnchor();
      },
      cancelDraft: () => {
        briefSectionHighlights.get(sectionId)?.setDraft(null);
        clearPendingTextAnchor();
      },
    });
  }, [
    isRoot,
    textAnnotation,
    comment.id,
    pendingTextAnchor,
    setPendingTextAnchor,
    clearPendingTextAnchor,
    startCommentEdit,
  ]);

  // Cancelling discards the draft and restores the comment's persisted highlight.
  const handleEditCancel = useCallback(() => {
    endCommentEdit(comment.id);
    pendingTextAnchor?.cancelDraft();
    if (isRoot && textAnnotation && !textAnnotation.detached) {
      briefSectionHighlights.get(textAnnotation.sectionId)?.upsert({
        commentId: comment.id,
        from: textAnnotation.from,
        to: textAnnotation.to,
      });
    }
  }, [pendingTextAnchor, isRoot, textAnnotation, comment.id, endCommentEdit]);

  // A live text range has a precise target of its own — the TipTap mark, which the section
  // editor scrolls to. Only the anchors with no mark need the section: a whole-section comment,
  // and a text anchor whose text has since been deleted.
  const fieldAnnotation = parsed?.kind === 'field' ? parsed : null;
  // `isDetached` is only ever true when there IS a text annotation, so this reads its section
  // directly rather than re-testing for one.
  const sectionScrollTarget = fieldAnnotation
    ? fieldAnnotation.sectionId
    : isDetached
      ? textAnnotation.sectionId
      : null;

  const handleSelect = useCallback(() => {
    if (!isRoot) return;
    // Scroll even when this comment is already selected: the reader may have scrolled away,
    // and clicking it again is a request to go back to it.
    if (sectionScrollTarget) {
      scrollBriefSectionIntoView(sectionScrollTarget, { reducedMotion: !!shouldReduceMotion });
    }
    if (isSelected) return;
    selectComment(comment.id);
  }, [isRoot, isSelected, selectComment, comment.id, sectionScrollTarget, shouldReduceMotion]);

  const { replies, isLoadingReplies } = useThreadReplies(comment, isRoot, isSelected);
  const {
    handleConfirmDelete,
    handleReplySave,
    handleEditSave,
    handleCopyLink,
    handleResolve,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
  } = useThreadActions(comment, actions);

  const isResolved = comment.status === 'resolved';
  const showHighlightChip = isRoot && hasActiveAnchor && !isResolved;
  const showHighlightRemoved = isRoot && isDetached && !isResolved;

  const handleResolveClick = useCallback(() => {
    if (!isResolved && isSelected) {
      clearSelectedComment();
    }
    handleResolve();
  }, [isResolved, isSelected, clearSelectedComment, handleResolve]);

  const repliesSlot =
    isRoot && isSelected && (replies.length > 0 || isLoadingReplies) ? (
      <div className="flex flex-col gap-2">
        {replies.map((reply, index) => (
          <div key={reply.id} className="ml-7">
            <BriefCommentThread
              comment={reply}
              depth={1}
              actions={actions}
              mentionOptions={mentionOptions}
              isFirstReply={index === 0}
            />
          </div>
        ))}
        {isLoadingReplies && <CommentRepliesSkeleton />}
      </div>
    ) : null;

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        data-comment-id={isRoot ? comment.id : undefined}
        onClick={isRoot ? handleSelect : undefined}
        className={commentCardClassName({ isRoot, isSelected, isFirstReply })}
      >
        <BriefComment
          authorName={authorName}
          authorAvatarUrl={avatarUrl ?? undefined}
          avatarSlot={agentIdentity?.avatarSlot}
          footerSlot={actions.renderCommentFooter?.(comment)}
          currentUserName={currentUser?.name}
          timestamp={formatRelativeTime(comment.createdAt)}
          content={comment.content ?? EMPTY_DOC}
          attachments={comment.attachments}
          replyCount={comment.replyCount}
          selected={isSelected}
          annotationCount={showHighlightChip ? 1 : 0}
          sectionAnchorName={
            fieldAnnotation ? actions.resolveSectionName?.(fieldAnnotation.sectionId) : undefined
          }
          highlightRemoved={showHighlightRemoved}
          onCopyLink={isRoot ? () => void handleCopyLink() : undefined}
          onDelete={canDelete ? () => setShowDeleteDialog(true) : undefined}
          resolved={isRoot && isResolved}
          onResolve={isRoot ? handleResolveClick : undefined}
          resolveDisabled={!canComment}
          onEditStart={isAuthor && canComment ? handleEditStart : undefined}
          renderReplyInput={
            isRoot && canComment
              ? () => (
                  <AnnotatedCommentInput
                    placeholder={t('commentOnFile.briefCommentThread.leaveYourReply')}
                    mentionOptions={mentionOptions}
                    showAnnotationToolbar={false}
                    clearSoloOnFocus={false}
                    onFocus={() => pendingTextAnchor?.cancelDraft()}
                    onSave={handleReplySave}
                  />
                )
              : undefined
          }
          renderEditInput={
            isAuthor && canComment
              ? ({ initialContent, initialAttachments, onCancel, onSaved }) => (
                  <AnnotatedCommentInput
                    placeholder={t('commentOnFile.briefCommentThread.editYourComment')}
                    submitLabel={t('commentOnFile.briefCommentThread.save')}
                    initialContent={initialContent}
                    initialAttachments={initialAttachments}
                    isEditing
                    mentionOptions={mentionOptions}
                    showAnnotationToolbar={false}
                    composerPrefix={
                      isRoot && pendingTextAnchor ? <HighlightCommentComposerChip /> : undefined
                    }
                    onCancel={() => {
                      handleEditCancel();
                      onCancel();
                    }}
                    onSave={async (content, _annotation, attachmentAssetIds) => {
                      const anchor = isRoot ? pendingTextAnchor : null;
                      // Re-anchored → new range; cleared (had an anchor, none now)
                      // → keep the comment but flag detached (unanchored).
                      /* eslint-disable i18next/no-literal-string -- annotation kind wire enum value */
                      const nextAnnotation = anchor
                        ? {
                            kind: 'text' as const,
                            sectionId: anchor.sectionId,
                            from: anchor.from,
                            to: anchor.to,
                          }
                        : isRoot && textAnnotation && !isDetached
                          ? {
                              kind: 'text' as const,
                              sectionId: textAnnotation.sectionId,
                              from: textAnnotation.from,
                              to: textAnnotation.to,
                              detached: true,
                            }
                          : undefined;
                      /* eslint-enable i18next/no-literal-string */
                      await handleEditSave(content, nextAnnotation, attachmentAssetIds);
                      if (anchor) anchor.applyMark(comment.id);
                      endCommentEdit(comment.id);
                      onSaved();
                    }}
                  />
                )
              : undefined
          }
        >
          {repliesSlot}
        </BriefComment>
      </div>

      <DeleteConfirmationModal
        open={showDeleteDialog}
        isDeleting={isDeleting}
        onCancel={() => {
          setShowDeleteDialog(false);
        }}
        onConfirmDelete={() => {
          void handleConfirmDelete();
        }}
        resourceType={tCommon('components.deleteConfirmationModal.comment')}
        warningMessage={t('commentOnFile.briefCommentThread.thisWillPermanentlyDeleteThe')}
      />
    </div>
  );
}
